import { useEffect, useMemo, useRef, useState } from 'react';

import { SYNC_LISTINGS_STATUS } from 'views/V2/Integration/constants';
import { toNumber } from 'views/V2/Integration/utils';

import { useLazyGetSyncListingsStatusQuery } from 'redux/slices/globalApi';

const { COMPLETED, FAILED } = SYNC_LISTINGS_STATUS;

function normalizeStatus(status) {
    return typeof status === 'string' ? status.toLowerCase() : status;
}

function calculatePercent(importedListings, totalListings) {
    // If totalListings is null/undefined, we cannot compute progress
    if (totalListings == null) return null;

    const total = toNumber(totalListings);
    const imported = toNumber(importedListings);

    if (total === null || imported === null || imported < 0) return null;

    if (total === 0) {
        return imported === 0 ? 0 : null;
    }

    const percent = (imported / total) * 100;
    return Math.min(100, Math.max(0, percent));
}

/**
 * Polls sync-listing status until terminal status (completed/failed).
 * Keeps *only* sync-related concerns here (no listings fetching).
 */
export default function useSyncListingsStatusPolling({
    integrationName,
    enabled,
    pollingMs = 5000,
    stopAfterConsecutiveNullStatus = 3,
    onNullStatusThresholdReached,
}) {
    const [trigger, { isFetching }] = useLazyGetSyncListingsStatusQuery();

    const [statusData, setStatusData] = useState(null);
    const [isResolved, setIsResolved] = useState(false);
    const [isStopped, setIsStopped] = useState(false);
    const [shouldFetchListings, setShouldFetchListings] = useState(false);
    const intervalRef = useRef(null);
    const inFlightRef = useRef(false);
    const consecutiveNullStatusRef = useRef(0);

    // reset when integration changes or disabled
    useEffect(() => {
        if (!enabled) {
            setStatusData(null);
            setIsResolved(false);
            setIsStopped(false);
            setShouldFetchListings(false);
            consecutiveNullStatusRef.current = 0;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        setStatusData(null);
        setIsResolved(false);
        setIsStopped(false);
        setShouldFetchListings(false);
        consecutiveNullStatusRef.current = 0;
    }, [enabled, integrationName]);

    useEffect(() => {
        if (!enabled || isResolved || isStopped) return;

        const poll = async () => {
            if (inFlightRef.current) return;
            inFlightRef.current = true;

            try {
                const result = await trigger({ integrationName }).unwrap();

                setStatusData(result ? { ...result } : null);

                if (result?.status == null) {
                    consecutiveNullStatusRef.current += 1;

                    if (
                        Number.isFinite(stopAfterConsecutiveNullStatus) &&
                        stopAfterConsecutiveNullStatus > 0 &&
                        consecutiveNullStatusRef.current >= stopAfterConsecutiveNullStatus
                    ) {
                        setIsStopped(true);
                        setShouldFetchListings(true);
                        onNullStatusThresholdReached?.();
                        return;
                    }
                } else {
                    consecutiveNullStatusRef.current = 0;
                }

                const status = normalizeStatus(result?.status);

                if (status === COMPLETED || status === FAILED) {
                    setIsResolved(true);
                }
            } catch (e) {
                // ignore; next tick will retry
            } finally {
                inFlightRef.current = false;
            }
        };

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        poll();
        intervalRef.current = setInterval(poll, pollingMs);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [
        enabled,
        trigger,
        integrationName,
        pollingMs,
        isResolved,
        isStopped,
        stopAfterConsecutiveNullStatus,
        onNullStatusThresholdReached,
    ]);

    // stop interval immediately once resolved
    useEffect(() => {
        if (!isResolved && !isStopped) return;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, [isResolved, isStopped]);

    const progressPercent = useMemo(() => {
        return calculatePercent(statusData?.importedListings, statusData?.totalListings);
    }, [statusData]);

    const showSyncProgress = Boolean(enabled && !isResolved && !isStopped && statusData !== null);

    return {
        statusData,
        isFetching,
        isResolved,
        isStopped,
        showSyncProgress,
        progressPercent,
        shouldFetchListings,
    };
}
